(() => {
  const ACCOUNT_SELECTOR = 'shopify-account.hp-shopify-account';
  const STYLE_ID = 'hp-account-component-style';
  const LOGIN_STYLE_ID = 'hp-account-login-style';
  const observedLoginRoots = new WeakSet();
  const pendingLoginForms = new WeakSet();
  const observedDialogs = new WeakSet();
  let keyboardMode = false;

  document.addEventListener('keydown', () => {
    keyboardMode = true;
  }, { capture: true });

  document.addEventListener('pointerdown', () => {
    keyboardMode = false;
  }, { capture: true, passive: true });

  const accountStyles = `
    :host {
      color: inherit;
      touch-action: manipulation;
      -webkit-tap-highlight-color: transparent;
    }

    /* Shopify creates this nested shadow root lazily on the first opening. */
    shopify-login-form:not([data-hp-login-ready]) {
      visibility: hidden !important;
    }

    .account-button {
      position: relative !important;
      width: var(--shopify-account-avatar-size, 48px) !important;
      height: var(--shopify-account-avatar-size, 48px) !important;
      min-width: var(--shopify-account-avatar-size, 48px) !important;
      min-height: var(--shopify-account-avatar-size, 48px) !important;
      padding: 0 !important;
      border: 0 !important;
      border-radius: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      color: inherit !important;
      overflow: visible !important;
      isolation: isolate !important;
      touch-action: manipulation !important;
      -webkit-tap-highlight-color: transparent !important;
      transition: color 105ms ease, transform 105ms ease !important;
    }

    .account-button > *:not(.hp-account-control-face) {
      opacity: 0 !important;
    }

    .account-button::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: var(--hp-account-glyph-size,27px);
      height: var(--hp-account-glyph-size,27px);
      z-index: 2;
      transform: translate(-50%,-50%);
      background: currentColor;
      -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='8' r='3.6' fill='none' stroke='black' stroke-width='2.2'/%3E%3Cpath d='M4.8 20c.6-4.1 3-6.2 7.2-6.2s6.6 2.1 7.2 6.2' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center / contain no-repeat;
      mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='8' r='3.6' fill='none' stroke='black' stroke-width='2.2'/%3E%3Cpath d='M4.8 20c.6-4.1 3-6.2 7.2-6.2s6.6 2.1 7.2 6.2' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center / contain no-repeat;
      pointer-events: none;
    }

    .account-button::after {
      content: '';
      position: absolute;
      z-index: 0;
      top: 50%;
      left: 50%;
      width: var(--shopify-account-avatar-size,48px);
      height: var(--shopify-account-avatar-size,48px);
      background: var(--hp-c15-control-shadow,#000);
      clip-path: polygon(
        2% 0, 100% 0,
        96% 25%, 100% 50%, 95% 75%, 98% 100%,
        0 100%,
        4% 75%, 0 50%, 3% 25%
      );
      opacity: 0;
      transform: translate(calc(-50% + 4px),calc(-50% + 4px)) scale(.88) rotate(-2deg);
      transform-origin: center;
      pointer-events: none;
      transition: opacity 90ms ease, transform 105ms ease;
    }

    .hp-account-control-face {
      position: absolute;
      z-index: 1;
      top: 50%;
      left: 50%;
      width: var(--shopify-account-avatar-size,48px);
      height: var(--shopify-account-avatar-size,48px);
      background: var(--hp-c15-control-face,#070707);
      clip-path: polygon(
        2% 0, 100% 0,
        96% 25%, 100% 50%, 95% 75%, 98% 100%,
        0 100%,
        4% 75%, 0 50%, 3% 25%
      );
      opacity: 0;
      transform: translate(-50%,-50%) scale(.88) rotate(-2deg);
      transform-origin: center;
      pointer-events: none;
      transition: opacity 90ms ease, transform 105ms ease;
    }

    .account-button:focus-visible {
      background: transparent !important;
      outline: 3px solid var(--hp-c15-acid,#d9f646) !important;
      outline-offset: 2px !important;
    }

    :host([data-hp-account-keyboard-focus]:not([data-hp-account-open])) .account-button {
      color: var(--hp-c15-control-face-ink,#fff) !important;
    }

    :host([data-hp-account-keyboard-focus]:not([data-hp-account-open])) .account-button::after {
      opacity: 1;
      transform: translate(calc(-50% + 4px),calc(-50% + 4px)) scale(1);
    }

    :host([data-hp-account-keyboard-focus]:not([data-hp-account-open])) .account-button .hp-account-control-face {
      opacity: 1;
      transform: translate(-50%,-50%) scale(1);
    }

    :host([data-hp-account-open]) .account-button {
      background: transparent !important;
      color: inherit !important;
      box-shadow: none !important;
    }

    @media (hover:hover) and (pointer:fine) {
      :host([data-hp-account-hover]:not([data-hp-account-open])) .account-button {
        background: transparent !important;
        color: var(--hp-c15-control-face-ink,#fff) !important;
      }

      :host([data-hp-account-hover]:not([data-hp-account-open])) .account-button::after {
        opacity: 1;
        transform: translate(calc(-50% + 4px),calc(-50% + 4px)) scale(1);
      }

      :host([data-hp-account-hover]:not([data-hp-account-open])) .account-button .hp-account-control-face {
        opacity: 1;
        transform: translate(-50%,-50%) scale(1);
      }
    }

    .account-button:active {
      background: transparent !important;
      color: var(--hp-c15-control-face-ink,#fff) !important;
      transform: translateY(1px) scale(.96) !important;
    }

    .account-button:active::after {
      opacity: 1;
      transform: translate(calc(-50% + 4px),calc(-50% + 4px)) scale(.96);
    }

    .account-button:active .hp-account-control-face {
      opacity: 1;
      transform: translate(calc(-50% + 2px),calc(-50% + 2px)) scale(.96);
    }

    .dialog {
      --hp-account-side-open-duration: 160ms;
      --hp-account-side-close-duration: 140ms;
      --dialog-drawer-opening-animation: hp-account-side-in !important;
      --dialog-drawer-closing-animation: hp-account-side-out !important;
      --dialog-drawer-easing: cubic-bezier(.32,.72,0,1) !important;
      position: fixed !important;
      inset: 0 calc(100% - 100vw) 0 auto !important;
      width: min(100vw,460px) !important;
      height: 100vh !important;
      height: 100dvh !important;
      max-width: none !important;
      max-height: none !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      border-left: 1px solid rgba(255,255,255,.14) !important;
      border-radius: 0 !important;
      background: #242424 !important;
      color: #fff !important;
      box-shadow: -.6rem 0 2rem rgba(0,0,0,.48) !important;
      overflow: hidden !important;
    }

    .dialog[open]:not(.closing) {
      animation-duration: var(--hp-account-side-open-duration) !important;
    }

    .dialog.closing {
      animation-duration: var(--hp-account-side-close-duration) !important;
    }

    @keyframes hp-account-side-in {
      from {
        opacity: 0;
        transform: translate3d(100%,0,0);
      }
      to {
        opacity: 1;
        transform: translate3d(0,0,0);
      }
    }

    @keyframes hp-account-side-out {
      from {
        opacity: 1;
        transform: translate3d(0,0,0);
      }
      to {
        opacity: 0;
        transform: translate3d(100%,0,0);
      }
    }

    .dialog::backdrop {
      background: rgba(0,0,0,.62) !important;
      -webkit-backdrop-filter: none !important;
      backdrop-filter: none !important;
    }

    .account {
      position: relative !important;
      width: 100% !important;
      height: 100% !important;
      max-height: none !important;
      display: grid !important;
      grid-template-rows: auto minmax(0,1fr) !important;
      border: 0 !important;
      border-radius: 0 !important;
      background: #242424 !important;
      color: #fff !important;
      overflow: hidden !important;
      isolation: isolate !important;
    }

    .account::before {
      content: '';
      position: absolute;
      inset: 0;
      z-index: -1;
      background: url('https://cdn.shopify.com/s/files/1/0767/1719/5522/files/bg-texture.webp?v=1777292849') center/cover;
      opacity: .08;
      pointer-events: none;
    }

    .account__header-region {
      min-height: var(--hp-drawer-rail-height,88px) !important;
      margin: 0 !important;
      padding: 0 !important;
      border-bottom: 1px solid rgba(255,255,255,.18) !important;
      background: transparent !important;
    }

    .account__header {
      min-height: inherit !important;
      box-sizing: border-box !important;
      padding: var(--hp-header-safe-top,0px) 0 0 !important;
      display: grid !important;
      grid-template-columns: minmax(0,1fr) calc(var(--hp-drawer-close-size,44px) + var(--hp-drawer-rail-right,var(--hp-drawer-rail-x,24px))) !important;
      grid-template-rows: var(--hp-drawer-rail-content-height,var(--hp-drawer-rail-height,88px)) !important;
      gap: 0 !important;
      align-items: center !important;
      background: transparent !important;
    }

    .hp-account-drawer-label {
      grid-column: 1 !important;
      grid-row: 1 !important;
      min-width: 0;
      margin: 0;
      padding-left: var(--hp-drawer-content-left,var(--hp-drawer-content-x,24px));
      color: #fff;
      font-family: 'Bowlby One SC',sans-serif;
      font-size: var(--hp-drawer-title-size,2rem);
      font-weight: 400;
      line-height: 1;
      text-transform: uppercase;
    }

    .account__header.three-column {
      grid-template-columns:
        calc(var(--hp-drawer-close-size,44px) + var(--hp-drawer-rail-left,var(--hp-drawer-rail-x,24px)))
        minmax(0,1fr)
        calc(var(--hp-drawer-close-size,44px) + var(--hp-drawer-rail-right,var(--hp-drawer-rail-x,24px))) !important;
      grid-template-rows: var(--hp-drawer-rail-content-height,var(--hp-drawer-rail-height,88px)) !important;
    }

    .account__header.three-column .account__back-button {
      grid-column: 1 !important;
      grid-row: 1 !important;
      justify-self: start !important;
      width: var(--hp-drawer-close-size,44px) !important;
      height: var(--hp-drawer-close-size,44px) !important;
      min-width: var(--hp-drawer-close-size,44px) !important;
      min-height: var(--hp-drawer-close-size,44px) !important;
      margin-left: var(--hp-drawer-rail-left,var(--hp-drawer-rail-x,24px)) !important;
      padding: calc((var(--hp-drawer-close-size,44px) - var(--hp-drawer-close-icon,24px)) / 2 - 1.5px) !important;
      border: 1.5px solid #000 !important;
      border-radius: 50% !important;
      background: #fff !important;
      color: #000 !important;
      box-shadow: none !important;
    }

    .account__header.three-column > .account__heading {
      grid-column: 2 !important;
      grid-row: 1 !important;
      min-width: 0 !important;
      overflow: hidden !important;
      font-size: var(--hp-drawer-title-size,2rem) !important;
      line-height: 1 !important;
      text-align: center !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
    }

    .account__header.three-column .account__close {
      grid-column: 3 !important;
    }

    .account__header-content {
      display: none !important;
    }

    .account__close {
      position: relative !important;
      grid-column: 2 !important;
      grid-row: 1 !important;
      justify-self: end !important;
      align-self: center !important;
      width: var(--hp-drawer-close-size,44px) !important;
      height: var(--hp-drawer-close-size,44px) !important;
      min-width: var(--hp-drawer-close-size,44px) !important;
      min-height: var(--hp-drawer-close-size,44px) !important;
      margin-right: var(--hp-drawer-rail-right,var(--hp-drawer-rail-x,24px)) !important;
      padding: 0 !important;
      border: 1.5px solid #000 !important;
      border-radius: 50% !important;
      background: #fff !important;
      color: #000 !important;
      box-shadow: none !important;
      overflow: visible !important;
      transition: background-color 120ms ease, color 120ms ease, transform 120ms ease !important;
    }

    .account__close > * {
      opacity: 0 !important;
    }

    .account__close::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: var(--hp-drawer-close-icon,24px);
      height: var(--hp-drawer-close-icon,24px);
      transform: translate(-50%,-50%);
      background: currentColor;
      -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M5 5l14 14M19 5 5 19' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round'/%3E%3C/svg%3E") center/contain no-repeat;
      mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M5 5l14 14M19 5 5 19' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round'/%3E%3C/svg%3E") center/contain no-repeat;
      pointer-events: none;
    }

    @media (hover:hover) and (pointer:fine) {
      .account__header.three-column .account__back-button:hover,
      .account__close:hover {
        background: var(--hp-c15-coral,#ff6947) !important;
        color: #000 !important;
      }
    }

    .account__close:focus-visible {
      background: #fff !important;
      color: #000 !important;
      outline: 2px solid #fff !important;
      outline-offset: 4px !important;
    }

    .account__header.three-column .account__back-button:focus-visible {
      background: #fff !important;
      color: #000 !important;
      outline: 3px solid var(--hp-c15-acid,#d9f646) !important;
      outline-offset: 2px !important;
    }

    .account__close:active {
      background: var(--hp-c15-acid,#d9f646) !important;
      color: #000 !important;
      transform: translateY(1px) scale(.96) !important;
    }

    .account__header.three-column .account__back-button:active {
      background: var(--hp-c15-acid,#d9f646) !important;
      color: #000 !important;
      transform: translateY(1px) scale(.96) !important;
    }

    .account__content {
      --hp-account-edge-gap: clamp(.75rem,2svh,1.15rem);
      min-height: 0 !important;
      margin-top: 0 !important;
      padding:
        var(--hp-account-edge-gap)
        var(--hp-drawer-content-right,var(--hp-drawer-content-x,24px))
        max(1rem,env(safe-area-inset-bottom))
        var(--hp-drawer-content-left,var(--hp-drawer-content-x,24px)) !important;
      display: flex !important;
      flex-direction: column !important;
      justify-content: flex-start !important;
      gap: 0 !important;
      overflow: hidden !important;
      background: transparent !important;
      color: #fff !important;
    }

    .hp-account-brand {
      flex: 0 0 auto;
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: clamp(1.25rem,3svh,1.8rem);
      padding: 0;
    }

    .hp-account-brand__logo-crop {
      position: relative;
      width: clamp(5.9rem,28.1vw,7.95rem);
      aspect-ratio: 490 / 307;
      overflow: visible;
    }

    .hp-account-brand__logo-crop img {
      width: 100%;
      max-width: 100%;
      height: 100%;
      display: block;
      object-fit: contain;
      filter: invert(1);
    }

    .hp-account-brand h2 {
      margin: clamp(1rem,2.5svh,1.45rem) 0 0;
      color: #fff;
      font-family: 'DM Mono',monospace;
      font-size: clamp(1.15rem,4.8vw,1.45rem);
      font-weight: 700;
      line-height: 1.2;
      text-align: center;
    }

    .account__login-slot {
      flex: 0 0 auto !important;
      margin: 0 !important;
      padding: 0 !important;
      background: transparent !important;
    }

    :host([data-customer-logged-in='false']) .account__content {
      overflow-y: auto !important;
      overscroll-behavior-y: contain !important;
    }

    :host([data-customer-logged-in='false']) .account__menu-region {
      display: none !important;
    }

    .hp-account-content--server-menu > .account__menu-region {
      display: none !important;
    }

    .hp-account-menu {
      display: flex !important;
      flex-direction: column !important;
      flex: 0 0 auto;
      min-width: 0;
    }

    :host([data-customer-logged-in='true']) .hp-account-brand {
      display: none !important;
    }

    :host([data-customer-logged-in='true']) .account__header.hp-account-header--main {
      grid-template-rows: var(--hp-drawer-rail-content-height,var(--hp-drawer-rail-height,88px)) auto !important;
    }

    :host([data-customer-logged-in='true']) .account__header.hp-account-header--main .account__header-content {
      --hp-account-section-gap: 1rem;
      --hp-account-name-email-gap: .125rem;
      grid-column: 1 / -1 !important;
      grid-row: 2 !important;
      min-width: 0 !important;
      max-width: none !important;
      display: flex !important;
      flex-direction: column !important;
      gap: 0 !important;
      margin: 0 !important;
      padding:
        calc(var(--hp-account-section-gap) - 1px)
        var(--hp-drawer-content-right,var(--hp-drawer-content-x,24px))
        var(--hp-account-section-gap)
        var(--hp-drawer-content-left,var(--hp-drawer-content-x,24px)) !important;
      border-top: 1px solid rgba(255,255,255,.18) !important;
    }

    :host([data-customer-logged-in='true']) .account__heading {
      margin: 0 0 var(--hp-account-name-email-gap,.125rem) !important;
      color: #fff !important;
      font-family: 'Bowlby One SC',sans-serif !important;
      font-size: clamp(1.5rem,5.4vw,2rem) !important;
      font-weight: 400 !important;
      line-height: 1.05 !important;
      text-transform: uppercase !important;
    }

    :host([data-customer-logged-in='true']) .account__header.three-column > .account__heading {
      font-size: var(--hp-drawer-title-size,2rem) !important;
    }

    :host([data-customer-logged-in='true']) .account__email {
      max-width: 100% !important;
      margin: 0 0 var(--hp-account-section-gap,1rem) !important;
      overflow: hidden !important;
      color: rgba(255,255,255,.68) !important;
      font-family: 'DM Mono',monospace !important;
      font-size: max(16px,.88rem) !important;
      line-height: 1.25 !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
    }

    :host([data-customer-logged-in='true']) .account__content {
      margin-top: 0 !important;
      padding-right: var(--hp-drawer-content-right,var(--hp-drawer-content-x,24px)) !important;
      padding-left: var(--hp-drawer-content-left,var(--hp-drawer-content-x,24px)) !important;
      overflow-x: hidden !important;
      overflow-y: hidden !important;
      overscroll-behavior: none !important;
      justify-content: flex-start !important;
    }

    .hp-account-member-card {
      flex: 0 0 auto;
      margin: 0 0 clamp(1rem,2.6svh,1.4rem);
      padding: clamp(1rem,2.5svh,1.25rem);
      border: 1px solid rgba(0,0,0,.9);
      background: rgba(255,255,255,.065);
      box-shadow: none;
      color: #fff;
    }

    :host([data-customer-logged-in='true']) .account__header-content .hp-account-member-card {
      width: 100%;
      box-sizing: border-box;
      margin: 0;
    }

    .hp-account-member-card__top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 1rem;
    }

    .hp-account-member-card__eyebrow {
      margin: 0 0 .35rem;
      color: rgba(255,255,255,.58);
      font: 700 .66rem/1.2 'DM Mono',monospace;
      letter-spacing: .08em;
      text-transform: uppercase;
    }

    .hp-account-member-card__tier {
      margin: 0;
      color: #fff;
      font: 400 clamp(1.15rem,4.6vw,1.5rem)/1 'Bowlby One SC',sans-serif;
      text-transform: uppercase;
    }

    .hp-account-member-card__discount {
      flex: 0 0 auto;
      padding: .42rem .55rem;
      border: 1px solid #000;
      background: var(--hp-c15-coral,#ff6947);
      color: #000;
      font: 700 .68rem/1 'DM Mono',monospace;
      text-transform: uppercase;
    }

    .hp-account-member-card__progress {
      margin-top: .95rem;
      padding-top: .85rem;
      border-top: 1px solid rgba(255,255,255,.18);
    }

    .hp-account-member-card__progress-copy {
      display: flex;
      justify-content: space-between;
      gap: .75rem;
      color: rgba(255,255,255,.72);
      font: 700 .65rem/1.3 'DM Mono',monospace;
    }

    .hp-account-member-card__progress-track {
      height: 7px;
      margin-top: .55rem;
      border: 1px solid rgba(255,255,255,.32);
      background: rgba(0,0,0,.35);
      overflow: hidden;
    }

    .hp-account-member-card__progress-fill {
      display: block;
      width: var(--hp-account-progress,0%);
      height: 100%;
      background: var(--hp-c15-acid,#d9f646);
    }

    .hp-account-member-card__next {
      margin: .5rem 0 0;
      color: rgba(255,255,255,.52);
      font: 500 .62rem/1.35 'DM Mono',monospace;
    }

    :host([data-customer-logged-in='true']) .account__menu-region {
      flex: 0 0 auto !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      background: transparent !important;
    }

    :host([data-customer-logged-in='true']) .account__menu-items,
    :host([data-customer-logged-in='true']) .account__shortcuts {
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      border-radius: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      filter: none !important;
    }

    :host([data-customer-logged-in='true']) .account__item-wrapper {
      --edge-padding: 0px !important;
      min-height: 3.35rem !important;
      overflow: hidden !important;
    }

    :host([data-customer-logged-in='true']) .account__item {
      min-height: 3.35rem !important;
      padding: 0 .2rem !important;
      border-color: rgba(255,255,255,.18) !important;
      color: #fff !important;
      font-family: 'DM Mono',monospace !important;
      font-size: max(16px,.95rem) !important;
      font-weight: 700 !important;
    }

    :host([data-customer-logged-in='true']) .account__item.hp-account-item--decorated {
      display: grid !important;
      grid-template-columns: 1.55rem minmax(0,1fr) auto !important;
      align-items: center !important;
      gap: .85rem !important;
      text-align: left !important;
    }

    .hp-account-item-icon {
      grid-column: 1;
      width: 1.45rem;
      height: 1.45rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: var(--hp-c15-coral,#ff6947);
    }

    .hp-account-item-icon svg {
      display: block;
      width: 100%;
      height: 100%;
      color: inherit !important;
    }

    .hp-account-item--decorated .account__item-text {
      grid-column: 2;
      min-width: 0;
    }

    .hp-account-signout {
      flex: 0 0 auto;
      min-height: 3.35rem;
      margin-top: var(--hp-account-edge-gap,clamp(.75rem,2svh,1.15rem));
      padding: 0 1rem;
      border: 1px solid #fff;
      background: transparent;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: .65rem;
      font: 700 .82rem/1 'DM Mono',monospace;
      text-decoration: none;
      text-transform: uppercase;
    }

    .hp-account-signout svg {
      width: 1.2rem;
      height: 1.2rem;
      color: var(--hp-c15-coral,#ff6947);
    }

    @media (hover:hover) and (pointer:fine) {
      .hp-account-signout:hover {
        border-color: #000;
        background: var(--hp-c15-coral,#ff6947);
        color: #000;
      }

      .hp-account-signout:hover svg {
        color: #000;
      }
    }

    .hp-account-signout:focus-visible {
      outline: 3px solid var(--hp-c15-acid,#d9f646);
      outline-offset: 2px;
    }

    @media (hover:none) and (pointer:coarse) {
      .account-button,
      .account-button::after,
      .hp-account-control-face,
      .account__header.three-column .account__back-button,
      .account__close {
        transition: none !important;
      }

      .dialog::backdrop {
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
      }

      .account-button:active {
        color: inherit !important;
        transform: none !important;
      }

      .account-button:focus-visible {
        color: inherit !important;
        outline: 0 !important;
        transform: none !important;
      }

      .account-button:active::after,
      .account-button:focus-visible::after,
      .account-button:active .hp-account-control-face,
      .account-button:focus-visible .hp-account-control-face {
        opacity: 0 !important;
      }

      .account__close:is(:focus-visible,:active),
      .account__header.three-column .account__back-button:is(:focus-visible,:active) {
        background: #fff !important;
        color: #000 !important;
        outline: 0 !important;
        transform: none !important;
      }
    }

    @media (max-width:1024px) {
      .account-button:focus-visible {
        color: inherit !important;
        outline: 0 !important;
        transform: none !important;
      }

      .account-button:focus-visible::after,
      .account-button:focus-visible .hp-account-control-face {
        opacity: 0 !important;
      }

      .account__close:is(:focus-visible,:active),
      .account__header.three-column .account__back-button:is(:focus-visible,:active) {
        background: #fff !important;
        color: #000 !important;
        outline: 0 !important;
        transform: none !important;
      }

      .dialog {
        --hp-account-side-open-duration: 0ms;
        --hp-account-side-close-duration: 0ms;
      }

      .dialog.closing {
        pointer-events: none !important;
      }

      .dialog.closing::backdrop {
        opacity: 0 !important;
        transition: none !important;
      }

      :host([data-customer-logged-in='false']) .account__content {
        --hp-account-edge-gap: calc(
          clamp(.8rem,2svh,1.2rem)
          + 50px
          + clamp(.55rem,1.4svh,.9rem)
          + clamp(2.25rem,6svh,3.75rem)
        );
      }
    }

    @media (max-width:767px) {
      .dialog {
        width: 100vw !important;
        border-left: 0 !important;
        box-shadow: none !important;
      }
    }

    @media (max-height:740px) {
      :host([data-customer-logged-in='true']) .account__content {
        --hp-account-edge-gap: .55rem;
        padding-block: var(--hp-account-edge-gap) max(.55rem,env(safe-area-inset-bottom)) !important;
      }

      .hp-account-brand {
        margin-bottom: .8rem;
      }

      .hp-account-brand__logo-crop {
        width: 4.75rem;
      }

      .hp-account-brand h2 {
        margin-top: .65rem;
        font-size: 1rem;
      }

    }

    @media (max-height:600px) {
      :host([data-customer-logged-in='false']) .account__content {
        --hp-account-edge-gap: calc(.55rem + 50px + .55rem + 1.25rem);
      }

      :host([data-customer-logged-in='true']) .account__header.hp-account-header--main .account__header-content {
        --hp-account-section-gap: .8rem;
        --hp-account-name-email-gap: .125rem;
        padding-block: calc(var(--hp-account-section-gap) - 1px) var(--hp-account-section-gap) !important;
      }

      :host([data-customer-logged-in='true']) .hp-account-member-card {
        padding: .7rem !important;
      }

      :host([data-customer-logged-in='true']) .hp-account-member-card__progress {
        margin-top: .6rem;
        padding-top: .55rem;
      }

      :host([data-customer-logged-in='true']) .account__item-wrapper,
      :host([data-customer-logged-in='true']) .account__item,
      :host([data-customer-logged-in='true']) .hp-account-signout {
        min-height: 3rem !important;
      }

      :host([data-customer-logged-in='true']) .hp-account-signout {
        margin-top: var(--hp-account-edge-gap) !important;
      }
    }

    @media (orientation:landscape) and (max-width:620px) and (max-height:600px) {
      :host([data-customer-logged-in='false']) .account__content {
        --hp-account-edge-gap: clamp(.75rem,6svh,1.25rem);
      }
    }

    @media (max-height:600px) and (min-width:621px) {
      :host([data-customer-logged-in='false']) .account__content {
        --hp-account-edge-gap: .55rem;
        display: flex !important;
        flex-direction: column !important;
        align-items: stretch !important;
        justify-content: center !important;
        gap: .85rem !important;
        padding-block: var(--hp-account-edge-gap) !important;
      }

      :host([data-customer-logged-in='false']) .hp-account-brand {
        display: grid !important;
        grid-template-columns: auto minmax(0,1fr) !important;
        align-items: center !important;
        gap: .85rem !important;
        margin: 0 !important;
      }

      :host([data-customer-logged-in='false']) .hp-account-brand h2 {
        margin: 0 !important;
        text-align: left !important;
      }

      :host([data-customer-logged-in='false']) .account__login-slot {
        width: 100% !important;
        min-width: 0 !important;
      }

      :host([data-customer-logged-in='true']) .account__header.hp-account-header--main .account__header-content {
        display: grid !important;
        grid-template-columns: minmax(8.5rem,.42fr) minmax(14rem,.58fr) !important;
        grid-template-rows: auto auto !important;
        align-items: center !important;
        column-gap: .85rem !important;
        row-gap: .2rem !important;
        padding-block: .4rem .45rem !important;
      }

      :host([data-customer-logged-in='true']) .account__heading {
        grid-column: 1 !important;
        grid-row: 1 !important;
        align-self: end !important;
        margin: 0 !important;
        font-size: 1.25rem !important;
      }

      :host([data-customer-logged-in='true']) .account__email {
        grid-column: 1 !important;
        grid-row: 2 !important;
        align-self: start !important;
        margin: 0 !important;
      }

      :host([data-customer-logged-in='true']) .account__header-content .hp-account-member-card {
        grid-column: 2 !important;
        grid-row: 1 / span 2 !important;
        margin: 0 !important;
        padding: .45rem !important;
      }

      :host([data-customer-logged-in='true']) .hp-account-member-card__tier {
        font-size: .88rem !important;
      }

      :host([data-customer-logged-in='true']) .hp-account-member-card__discount {
        padding: .32rem .42rem !important;
        font-size: .6rem !important;
      }

      :host([data-customer-logged-in='true']) .hp-account-member-card__progress {
        margin-top: .35rem !important;
        padding-top: .32rem !important;
      }

      :host([data-customer-logged-in='true']) .hp-account-member-card__progress-track {
        height: 5px !important;
        margin-top: .28rem !important;
      }

      :host([data-customer-logged-in='true']) .hp-account-member-card__next {
        display: none !important;
      }

      :host([data-customer-logged-in='true']) .account__content {
        --hp-account-edge-gap: .32rem;
        padding-block: var(--hp-account-edge-gap) !important;
      }

      :host([data-customer-logged-in='true']) .account__item-wrapper,
      :host([data-customer-logged-in='true']) .account__item,
      :host([data-customer-logged-in='true']) .hp-account-signout {
        min-height: 2.25rem !important;
      }

      :host([data-customer-logged-in='true']) .hp-account-signout {
        margin-top: var(--hp-account-edge-gap) !important;
      }
    }

    /* Approved compact sign-in layout; keep tablet and signed-in spacing. */
    @media (max-width:620px) {
      :host([data-customer-logged-in='false']) .account__content {
        --hp-account-edge-gap: 32px;
      }
    }

    @media (orientation:landscape) and (max-width:620px) and (max-height:600px) {
      :host([data-customer-logged-in='false']) .account__content {
        --hp-account-edge-gap: 16px;
      }
    }

    :host([data-customer-logged-in='false']) .dialog:focus {
      outline: none !important;
    }

    /* Retain keyboard visibility after the touch-device interaction resets. */
    .account__close:focus-visible {
      outline: 2px solid #fff !important;
      outline-offset: 4px !important;
    }

    @media (prefers-reduced-motion:reduce) {
      .dialog {
        --hp-account-side-open-duration: 0ms;
        --hp-account-side-close-duration: 0ms;
      }

      .account-button,
      .account__header.three-column .account__back-button,
      .account__close {
        transition: none !important;
        transform: none !important;
      }

      .account-button:active,
      .account__header.three-column .account__back-button:active,
      .account__close:active {
        transform: none !important;
      }

      .dialog[open],
      .dialog.closing {
        animation: none !important;
      }

      .dialog.closing {
        visibility: hidden !important;
        opacity: 0 !important;
        transform: translate3d(100%,0,0) !important;
      }

      .dialog.closing::backdrop {
        opacity: 0 !important;
        transition: none !important;
      }
    }
  `;

  const loginStyles = `
    .login-form__root {
      display: block !important;
      margin: 0 !important;
      padding: 0 !important;
      background: transparent !important;
    }

    ::slotted(shop-login),
    shop-login,
    .login-form__divider {
      display: none !important;
    }

    .login-form__email-form {
      margin: 0 !important;
      padding: 0 !important;
    }

    .login-form__email-input-wrapper {
      width: 100% !important;
      display: grid !important;
      grid-template-columns: minmax(0,1fr) !important;
      grid-template-rows: auto auto auto !important;
      gap: .75rem !important;
      overflow: visible !important;
      border: 0 !important;
      background: transparent !important;
    }

    .login-form__email-input {
      position: static !important;
      grid-row: 1 !important;
      width: 100% !important;
      height: 3.5rem !important;
      min-height: 3.5rem !important;
      margin: 0 !important;
      padding: 0 1rem !important;
      border: 1px solid #858585 !important;
      border-radius: 0 !important;
      outline: 0 !important;
      background: #fff !important;
      color: #000 !important;
      box-shadow: none !important;
      font-family: 'DM Mono',monospace !important;
      font-size: max(16px,.94rem) !important;
      font-weight: 700 !important;
      line-height: 1 !important;
    }

    .login-form__email-input:hover:not(:focus) {
      border-color: #858585 !important;
      box-shadow: inset 0 0 0 2px #858585 !important;
    }

    .login-form__email-input:focus {
      border-color: #ff6544 !important;
      box-shadow: inset 0 0 0 2px #ff6544 !important;
    }

    .login-form__email-input::placeholder {
      color: #686868 !important;
      opacity: 1 !important;
    }

    .login-form__email-label {
      display: none !important;
    }

    .login-form__email-submit {
      position: static !important;
      grid-row: 2 !important;
      width: 100% !important;
      height: 3.25rem !important;
      min-height: 3.25rem !important;
      margin: 0 !important;
      padding: 0 1rem !important;
      border: 0 !important;
      border-radius: 0 !important;
      background: #ff6544 !important;
      color: #000 !important;
      box-shadow: none !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      font-family: 'DM Mono',monospace !important;
      font-size: .92rem !important;
      font-weight: 700 !important;
      line-height: 1 !important;
      text-transform: uppercase !important;
    }

    .login-form__email-submit:disabled {
      opacity: .55 !important;
    }

    .login-form__email-submit-icon {
      display: none !important;
    }

    .hp-account-continue-label {
      display: inline !important;
    }

    .login-form__email-input-wrapper .hp-account-login-terms {
      grid-column: 1 / -1 !important;
      grid-row: 3 !important;
      margin: 0 !important;
      color: #c7c7c7 !important;
      font: 400 .75rem / 1.6 'DM Mono',monospace !important;
      text-align: center !important;
      text-transform: none !important;
    }

    .login-form__marketing-consent {
      grid-column: 1 / -1 !important;
      grid-row: 2 !important;
      display: grid !important;
      grid-template-columns: 18px minmax(0,1fr) !important;
      width: fit-content !important;
      max-width: 100% !important;
      justify-self: center !important;
      align-items: start !important;
      align-content: center !important;
      gap: 10px !important;
      min-height: 44px !important;
      margin: 0 !important;
      padding: 0 !important;
      color: #ddd !important;
      font: 400 .8125rem / 1.6 'DM Mono',monospace !important;
      text-align: center !important;
      cursor: pointer !important;
    }

    .login-form__marketing-consent-visual {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      box-sizing: border-box !important;
      width: 18px !important;
      height: 18px !important;
      margin: 1px 0 0 !important;
      border: 1.5px solid #a9a9a9 !important;
      border-radius: 2px !important;
      background: #242424 !important;
      color: #111 !important;
      box-shadow: none !important;
    }

    .login-form__marketing-consent-icon {
      display: block !important;
      width: 13px !important;
      height: 13px !important;
      opacity: 0 !important;
    }

    .login-form__marketing-consent-visual,
    .login-form__marketing-consent-icon {
      /* Shopify's native tick fade must not trail the checkbox fill. */
      transition: none !important;
    }

    .login-form__marketing-consent-checkbox:checked + .login-form__marketing-consent-visual {
      border-color: #fff !important;
      background: #fff !important;
    }

    .login-form__marketing-consent-checkbox:checked + .login-form__marketing-consent-visual .login-form__marketing-consent-icon {
      opacity: 1 !important;
    }

    .login-form__marketing-consent-checkbox:focus-visible + .login-form__marketing-consent-visual {
      outline: 3px solid #d9f646 !important;
      outline-offset: 3px !important;
    }

    .login-form__marketing-consent-label {
      color: inherit !important;
      font: inherit !important;
    }

    .login-form__email-input-wrapper p.hp-account-login-terms a {
      color: inherit !important;
      text-decoration: underline !important;
      text-decoration-color: currentColor !important;
      text-underline-offset: 2px !important;
    }

    .login-form__email-input-wrapper p.hp-account-login-terms a:is(:hover,:active,:focus-visible) {
      color: #fff !important;
      text-decoration: underline !important;
      text-decoration-color: currentColor !important;
    }

    .login-form__email-input-wrapper p.hp-account-login-terms a:focus-visible {
      outline: 3px solid #d9f646 !important;
      outline-offset: 3px !important;
    }

    @media (max-height:740px) {
      .login-form__email-input {
        height: 2.65rem !important;
        min-height: 2.65rem !important;
      }

      .login-form__email-submit {
        height: 2.5rem !important;
        min-height: 2.5rem !important;
      }

      .login-form__email-input-wrapper {
        gap: .4rem !important;
      }
    }

    @media (max-height:600px) and (min-width:621px) {
      .login-form__email-input-wrapper {
        grid-template-columns: minmax(0,1fr) clamp(8.25rem,31vw,9.5rem) !important;
        grid-template-rows: auto !important;
        align-items: stretch !important;
        gap: .55rem !important;
      }

      .login-form__email-input {
        grid-column: 1 !important;
        grid-row: 1 !important;
        height: 2.65rem !important;
        min-height: 2.65rem !important;
      }

      .login-form__email-submit {
        grid-column: 2 !important;
        grid-row: 1 !important;
        height: 2.65rem !important;
        min-height: 2.65rem !important;
      }

      .login-form__email-input-wrapper .hp-account-login-terms {
        grid-row: 2 !important;
      }
    }

    /* Keep consent before Continue in the reading, keyboard and visual order. */
    .login-form__email-input-wrapper:has(.login-form__marketing-consent) {
      grid-template-columns: minmax(0,1fr) !important;
      grid-template-rows: auto auto auto auto !important;
      gap: 1rem !important;
    }

    .login-form__email-input-wrapper:has(.login-form__marketing-consent) .login-form__email-submit {
      grid-column: 1 !important;
      grid-row: 3 !important;
    }

    .login-form__email-input-wrapper:has(.login-form__marketing-consent) .hp-account-login-terms {
      grid-row: 4 !important;
    }

    @media (max-height:740px) {
      .login-form__email-input-wrapper:has(.login-form__marketing-consent) {
        gap: .75rem !important;
      }
    }
  `;

  const appendStyle = (root, id, cssText) => {
    if (!root || root.getElementById?.(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = cssText;
    root.append(style);
  };

  const buildBrand = (component) => {
    const logoUrl = component.dataset.logoUrl || '';
    const brand = document.createElement('div');
    brand.className = 'hp-account-brand';
    brand.dataset.hpAccountBrand = '';
    brand.innerHTML = `
      <div class="hp-account-brand__logo-crop">
        <img src="${logoUrl}" alt="Halfpiped Skate Shop" width="490" height="307">
      </div>
      <h2>Sign In / Sign Up</h2>
    `;
    return brand;
  };

  const accountItemIcons = {
    profile: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="3.5"/><path d="M5 20c.6-4 2.9-6 7-6s6.4 2 7 6"/></svg>',
    orders: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m4 7 8-4 8 4-8 4-8-4Z"/><path d="M4 7v10l8 4 8-4V7M12 11v10"/></svg>',
    rewards: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3 9.4 8.3 3.5 9.2l4.3 4.2-1 5.9 5.2-2.8 5.2 2.8-1-5.9 4.3-4.2-5.9-.9L12 3Z"/></svg>',
  };

  const buildAccountItemIcon = (kind) => {
    const icon = document.createElement('span');
    icon.className = 'hp-account-item-icon';
    icon.dataset.hpAccountItemIcon = kind;
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = accountItemIcons[kind];
    return icon;
  };

  const getAccountItemKind = (item) => {
    const href = item.getAttribute('href') || item.closest('a')?.getAttribute('href') || '';
    const label = item.querySelector('.account__item-text')?.textContent || item.textContent || '';
    const normalizedHref = String(href).toLowerCase();
    const normalizedLabel = String(label).trim().toLowerCase();

    if (/(?:[?&]tab=profile\b|\/profile(?:[/?#]|$))/.test(normalizedHref) || normalizedLabel === 'profile') return 'profile';
    if (/(?:[?&]tab=orders\b|\/orders(?:[/?#]|$))/.test(normalizedHref) || normalizedLabel === 'orders') return 'orders';
    if (/(?:[?&]tab=rewards\b|\/rewards(?:[/?#]|$))/.test(normalizedHref) || normalizedLabel === 'rewards') return 'rewards';
    return '';
  };

  const decorateAccountItems = (accountRoot) => {
    accountRoot.querySelectorAll('.account__item').forEach((item) => {
      const kind = getAccountItemKind(item);
      if (!kind) return;

      item.classList.add('hp-account-item--decorated');
      item.dataset.hpAccountItemKind = kind;
      const existing = item.querySelector(':scope > [data-hp-account-item-icon]');
      if (existing?.dataset.hpAccountItemIcon === kind) return;
      existing?.remove();
      item.prepend(buildAccountItemIcon(kind));
    });
  };

  const syncAccountMenu = (component, accountRoot, accountContent, isMainSignedInView) => {
    let menu = accountRoot.querySelector('[data-hp-account-menu]');
    if (!isMainSignedInView) {
      menu?.remove();
      accountContent?.classList.remove('hp-account-content--server-menu');
      return;
    }
    if (!accountContent) return;

    if (!menu) {
      const template = component.parentElement?.querySelector('template[data-hp-account-menu-template]');
      const source = template?.content?.querySelector('[data-hp-account-menu]');
      if (source) {
        menu = source.cloneNode(true);
        const next = accountContent.querySelector('.account__menu-region, [data-hp-account-signout]');
        accountContent.insertBefore(menu, next);
      }
    }

    // Keep these links outside Shopify's asynchronously replaced menu region.
    // Late menu responses must not swap the layout or remove a focused link.
    accountContent.classList.toggle('hp-account-content--server-menu', Boolean(menu));
  };

  const buildMemberCard = (component) => {
    const tier = String(component.dataset.memberTierName || '').trim();
    const level = Math.max(0, Number.parseInt(component.dataset.memberLevel || '0', 10) || 0);
    const discount = String(component.dataset.memberDiscountLabel || '').trim();
    const card = document.createElement('section');
    card.className = 'hp-account-member-card';
    card.dataset.hpAccountMemberCard = '';
    const tierLabel = tier || 'Member';
    card.setAttribute('aria-label', level > 0 ? 'Member status' : 'Member pricing');

    const top = document.createElement('div');
    top.className = 'hp-account-member-card__top';
    const identity = document.createElement('div');
    const eyebrow = document.createElement('p');
    eyebrow.className = 'hp-account-member-card__eyebrow';
    eyebrow.textContent = level > 0 ? 'Member status' : 'Member pricing';
    const tierHeading = document.createElement('h3');
    tierHeading.className = 'hp-account-member-card__tier';
    tierHeading.textContent = level > 0 ? `Level ${level} ${tierLabel}` : tierLabel;
    identity.append(eyebrow, tierHeading);
    top.append(identity);

    if (discount) {
      const discountBadge = document.createElement('span');
      discountBadge.className = 'hp-account-member-card__discount';
      discountBadge.textContent = discount;
      top.append(discountBadge);
    }
    card.append(top);

    if (component.dataset.memberProgressAvailable === 'true') {
      const progressValue = Math.max(0, Math.min(100, Number(component.dataset.memberProgressPercent) || 0));
      const progressLabel = String(component.dataset.memberProgressLabel || `${progressValue}%`).trim();
      const nextLabel = String(component.dataset.memberNextLabel || '').trim();
      const progress = document.createElement('div');
      progress.className = 'hp-account-member-card__progress';
      progress.innerHTML = '<div class="hp-account-member-card__progress-copy"><span>Level progress</span><strong></strong></div><div class="hp-account-member-card__progress-track" role="progressbar" aria-valuemin="0" aria-valuemax="100"><span class="hp-account-member-card__progress-fill"></span></div>';
      progress.querySelector('strong').textContent = progressLabel;
      const track = progress.querySelector('[role="progressbar"]');
      track.setAttribute('aria-valuenow', String(Math.round(progressValue)));
      track.setAttribute('aria-label', progressLabel);
      progress.querySelector('.hp-account-member-card__progress-fill').style.setProperty('--hp-account-progress', `${progressValue}%`);
      if (nextLabel) {
        const next = document.createElement('p');
        next.className = 'hp-account-member-card__next';
        next.textContent = nextLabel;
        progress.append(next);
      }
      card.append(progress);
    }

    return card;
  };

  const buildSignOut = (url) => {
    const signOut = document.createElement('a');
    signOut.className = 'hp-account-signout';
    signOut.dataset.hpAccountSignout = '';
    signOut.href = url;
    signOut.target = '_top';
    signOut.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 5H5v14h5M14 8l4 4-4 4M18 12H9"/></svg><span>Sign out</span>';
    return signOut;
  };

  const decorateAccountView = (component, accountRoot) => {
    const loggedIn = component.dataset.customerLoggedIn === 'true';
    const header = accountRoot.querySelector('.account__header');
    const headerContent = header?.querySelector('.account__header-content');
    const isSubmenuHeader = Boolean(header?.classList.contains('three-column') || header?.querySelector('.account__back-button'));
    const loginSlot = accountRoot.querySelector('.account__login-slot');
    const isMainSignedInView = Boolean(loggedIn && !isSubmenuHeader && headerContent && loginSlot?.hidden);

    if (header) {
      let label = header.querySelector('[data-hp-account-drawer-label]');
      if (isSubmenuHeader) {
        label?.remove();
      } else if (!label) {
        label = document.createElement('p');
        label.className = 'hp-account-drawer-label';
        label.dataset.hpAccountDrawerLabel = '';
        header.prepend(label);
      }
      const labelText = 'Account';
      if (label && label.textContent !== labelText) label.textContent = labelText;
      header.classList.toggle('hp-account-header--main', isMainSignedInView);
    }

    const accountContent = accountRoot.querySelector('.account__content');
    syncAccountMenu(component, accountRoot, accountContent, isMainSignedInView);
    accountRoot.querySelectorAll('[data-hp-account-member-card], [data-hp-account-signout]').forEach((node) => {
      if (!isMainSignedInView) node.remove();
    });
    if (!isMainSignedInView || !accountContent || !headerContent) return;

    decorateAccountItems(accountRoot);

    let memberCard = accountRoot.querySelector('[data-hp-account-member-card]');
    if (component.dataset.memberSummaryAvailable === 'true') {
      memberCard ||= buildMemberCard(component);
      if (memberCard.parentElement !== headerContent) headerContent.append(memberCard);
    } else {
      memberCard?.remove();
    }

    const logoutUrl = String(component.dataset.logoutUrl || '').trim();
    if (logoutUrl && !accountRoot.querySelector('[data-hp-account-signout]')) {
      accountContent.append(buildSignOut(logoutUrl));
    }
  };

  const disableShopLookup = (loginForm) => {
    const providers = Array.isArray(loginForm.socialProviders) ? loginForm.socialProviders : [];
    const emailProviders = providers.filter(({ handle } = {}) => String(handle || '').toLowerCase() !== 'shop');
    const shopElements = Array.from(loginForm.querySelectorAll('shop-login'));

    if (emailProviders.length === providers.length && shopElements.length === 0) return;

    loginForm.socialProviders = emailProviders;
    shopElements.forEach((shopLogin) => {
      if (typeof shopLogin.dismiss === 'function') shopLogin.dismiss();
      shopLogin.remove();
    });

    if (typeof loginForm.reset === 'function') loginForm.reset();
  };

  const decorateLoginForm = (accountRoot) => {
    const loginForm = accountRoot.querySelector('shopify-login-form');
    if (!loginForm) return;

    disableShopLookup(loginForm);

    const loginRoot = loginForm.shadowRoot;
    if (!loginRoot) {
      if (!pendingLoginForms.has(loginForm)) {
        pendingLoginForms.add(loginForm);
        const prepareLogin = () => {
          if (!loginForm.isConnected) {
            pendingLoginForms.delete(loginForm);
            return;
          }
          if (!loginForm.shadowRoot) {
            window.setTimeout(prepareLogin, 40);
            return;
          }
          pendingLoginForms.delete(loginForm);
          decorateLoginForm(accountRoot);
        };
        if (window.customElements && !customElements.get('shopify-login-form')) {
          customElements.whenDefined('shopify-login-form').then(prepareLogin);
        } else {
          window.setTimeout(prepareLogin, 40);
        }
      }
      return;
    }

    appendStyle(loginRoot, LOGIN_STYLE_ID, loginStyles);

    const submit = loginRoot.querySelector('.login-form__email-submit');
    const consent = loginRoot.querySelector('.login-form__marketing-consent');
    if (consent && submit?.parentElement &&
        (consent.parentElement !== submit.parentElement || consent.nextElementSibling !== submit)) {
      // Move Shopify's label intact: its checkbox, consent state and native
      // listeners must remain owned by Shopify throughout lazy form rerenders.
      submit.parentElement.insertBefore(consent, submit);
    }
    if (submit && !submit.querySelector('.hp-account-continue-label')) {
      const label = document.createElement('span');
      label.className = 'hp-account-continue-label';
      label.textContent = 'Continue';
      submit.prepend(label);
    }

    if (submit?.parentElement) {
      let terms = loginRoot.querySelector('[data-hp-account-login-terms]');
      if (!terms) {
        terms = document.createElement('p');
        terms.className = 'hp-account-login-terms';
        terms.setAttribute('data-hp-account-login-terms', '');
        terms.innerHTML = 'By signing up, you agree to our <a href="https://www.halfpiped.com/pages/terms-and-conditions" target="_blank" rel="noopener noreferrer">terms of service</a> and <a href="https://www.halfpiped.com/pages/privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy</a>. You can manage marketing emails in your Account → Profile after signing in.';
      }
      if (terms.parentElement !== submit.parentElement) submit.parentElement.append(terms);
    }

    const email = loginRoot.querySelector('.login-form__email-input');
    if (email) email.placeholder = 'Email *';
    loginForm.setAttribute('data-hp-login-ready', '');

    if (!observedLoginRoots.has(loginRoot)) {
      const observer = new MutationObserver(() => decorateLoginForm(accountRoot));
      observer.observe(loginRoot, { childList: true, subtree: true });
      observedLoginRoots.add(loginRoot);
    }
  };

  const getDialog = (accountRoot) => accountRoot?.querySelector('.dialog');

  const dialogIsOpen = (dialog) => Boolean(
    dialog && (dialog.open || dialog.hasAttribute('open') || dialog.getAttribute('aria-hidden') === 'false')
  );

  const clearPointerFocus = (component) => {
    if (keyboardMode) return;
    const blurActiveElement = () => {
      // A queued pointer cleanup from closing must not blur a reopened modal.
      if (dialogIsOpen(getDialog(component.shadowRoot))) return;
      const shadowActive = component.shadowRoot?.activeElement;
      if (shadowActive instanceof HTMLElement) shadowActive.blur();
      if (document.activeElement === component && typeof component.blur === 'function') component.blur();
    };
    window.requestAnimationFrame(() => {
      blurActiveElement();
      window.requestAnimationFrame(blurActiveElement);
    });
  };

  const syncDialogState = (component) => {
    const accountRoot = component.shadowRoot;
    const dialog = getDialog(accountRoot);
    const isOpen = dialogIsOpen(dialog) && !dialog?.classList.contains('closing');
    const wasOpen = component.hasAttribute('data-hp-account-open');

    component.toggleAttribute('data-hp-account-open', isOpen);

    if (isOpen && !wasOpen) {
      component.removeAttribute('data-hp-account-hover');
      document.dispatchEvent(new CustomEvent('hp:account:opening'));
      const closeButton = accountRoot.querySelector('.account__close');
      const initialFocus = component.dataset.customerLoggedIn === 'false' ? dialog : closeButton;
      if (initialFocus instanceof HTMLElement) initialFocus.focus({ preventScroll: true });
    } else if (!isOpen && wasOpen) {
      clearPointerFocus(component);
    }

    if (dialog && !observedDialogs.has(dialog)) {
      const sync = () => window.requestAnimationFrame(() => syncDialogState(component));
      dialog.addEventListener('close', sync);
      dialog.addEventListener('cancel', sync);
      dialog.addEventListener('toggle', sync);
      const dialogObserver = new MutationObserver(sync);
      dialogObserver.observe(dialog, {
        attributes: true,
        attributeFilter: ['open', 'class', 'aria-hidden'],
      });
      observedDialogs.add(dialog);
    }
  };

  const closeAccount = (component) => {
    const accountRoot = component.shadowRoot;
    const dialog = getDialog(accountRoot);
    if (!dialogIsOpen(dialog)) return;

    const closeButton = accountRoot.querySelector('.account__close');
    if (closeButton instanceof HTMLElement) {
      closeButton.click();
    } else if (typeof dialog.close === 'function') {
      dialog.close();
    }
  };

  const decorate = (component) => {
    const accountRoot = component.shadowRoot;
    if (!accountRoot) return false;

    appendStyle(accountRoot, STYLE_ID, accountStyles);

    const accountButton = accountRoot.querySelector('.account-button');
    if (!accountButton) return false;

    accountRoot.querySelector('.account__close')?.removeAttribute('autofocus');
    if (component.dataset.customerLoggedIn === 'false') {
      getDialog(accountRoot)?.setAttribute('tabindex', '-1');
    }

    if (!accountButton.querySelector('.hp-account-control-face')) {
      const controlFace = document.createElement('span');
      controlFace.className = 'hp-account-control-face';
      controlFace.setAttribute('aria-hidden', 'true');
      accountButton.append(controlFace);
    }

    const accountContent = accountRoot.querySelector('.account__content');
    const loginSlot = accountRoot.querySelector('.account__login-slot');

    if (accountContent && loginSlot && !accountRoot.querySelector('[data-hp-account-brand]')) {
      accountContent.insertBefore(buildBrand(component), loginSlot);
    }

    accountRoot.querySelector('[data-hp-account-benefits]')?.remove();

    decorateAccountView(component, accountRoot);
    decorateLoginForm(accountRoot);
    syncDialogState(component);
    component.classList.add('hp-account-ready');
    return true;
  };

  const initializeComponent = (component) => {
    if (component.dataset.hpAccountInitializer === 'true') return;
    component.dataset.hpAccountInitializer = 'true';

    const clearTransientFaceState = () => {
      component.removeAttribute('data-hp-account-hover');
      component.removeAttribute('data-hp-account-keyboard-focus');
    };

    component.addEventListener('pointerenter', () => {
      if (!component.hasAttribute('data-hp-account-open')) {
        component.setAttribute('data-hp-account-hover', '');
      }
    });
    component.addEventListener('pointerleave', () => {
      component.removeAttribute('data-hp-account-hover');
    });
    component.addEventListener('pointercancel', () => {
      component.removeAttribute('data-hp-account-hover');
    });
    component.addEventListener('pointerdown', () => {
      component.removeAttribute('data-hp-account-keyboard-focus');
    });
    component.addEventListener('focusin', () => {
      component.toggleAttribute('data-hp-account-keyboard-focus', keyboardMode);
    });
    component.addEventListener('focusout', () => {
      component.removeAttribute('data-hp-account-keyboard-focus');
    });
    document.addEventListener('pointerdown', (event) => {
      if (event.composedPath().includes(component)) return;
      clearTransientFaceState();
      clearPointerFocus(component);
    }, { capture: true, passive: true });
    window.addEventListener('blur', clearTransientFaceState);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) clearTransientFaceState();
    });

    let observedRoot;
    const observer = new MutationObserver(() => decorateNow());
    const decorateNow = () => {
      if (!component.isConnected) return false;
      const root = component.shadowRoot;
      if (!root) return false;
      observedRoot = root;

      // Native first-open rendering can replace the shadow contents. Restore
      // styling in the same mutation checkpoint, before the browser paints.
      // Ignore our own decorations so they cannot create an observer loop.
      observer.disconnect();
      try {
        return decorate(component);
      } finally {
        observer.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['open', 'class', 'aria-hidden', 'hidden'],
        });
      }
    };

    const connectObserver = () => {
      if (!component.isConnected) return;
      decorateNow();
      // Observe an empty root too: the avatar and lazy dialog render later.
      if (!observedRoot) window.setTimeout(connectObserver, 40);
    };

    component.addEventListener('click', decorateNow, { capture: true });
    component.addEventListener('open', decorateNow);
    component.addEventListener('close', () => {
      component.removeAttribute('data-hp-account-open');
      window.setTimeout(() => {
        if (dialogIsOpen(getDialog(component.shadowRoot))) {
          syncDialogState(component);
          return;
        }
        component.removeAttribute('data-hp-account-open');
        clearPointerFocus(component);
        decorateNow();
      }, 260);
    });

    ['hp:menu:opening', 'hp:search:opening', 'hp:cart:opening'].forEach((eventName) => {
      document.addEventListener(eventName, () => closeAccount(component), { capture: true });
    });

    if (window.customElements && !customElements.get('shopify-account')) {
      customElements.whenDefined('shopify-account').then(connectObserver);
    } else {
      connectObserver();
    }
  };

  const initialize = () => {
    document.querySelectorAll(ACCOUNT_SELECTOR).forEach(initializeComponent);
  };

  // This asset runs before the header markup. Prepare each account as soon as
  // the parser inserts it, rather than waiting for every deferred page script.
  initialize();
  if (document.readyState === 'loading') {
    const parserObserver = new MutationObserver(initialize);
    parserObserver.observe(document.documentElement, { childList: true, subtree: true });
    document.addEventListener('DOMContentLoaded', () => {
      parserObserver.disconnect();
      initialize();
    }, { once: true });
  }
  document.addEventListener('shopify:section:load', initialize);
})();
